import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/videos", label: "Videos", feature: "youtube" },
  { to: "/journey", label: "The Journey", feature: "journey" },
  { to: "/fish-room", label: "Fish Room", feature: "fish_room" },
  { to: "/breeding", label: "Breeding", feature: "breeding" },
  { to: "/business", label: "Business", feature: "business" },
  { to: "/test-lab", label: "Test Lab", soon: true, feature: "test_lab" },
  { to: "/community", label: "Community", feature: "community" },
  { to: "/forum", label: "Forum", feature: "forum" },
  { to: "/journal", label: "Journal", feature: "journal" },
  { to: "/about", label: "About" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isEnabled } = useSiteSettings();
  const navItems = NAV.filter((item) => !item.feature || isEnabled(item.feature));

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass-nav" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between py-2">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <img src={IMAGES.logo} alt="The Fish House Project" className="h-40 w-40 md:h-48 md:w-48 object-contain" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-brand-cyan" : "text-brand-textSec hover:text-white"
                }`
              }
            >
              <span className="inline-flex items-center gap-1.5">
                {item.label}
                {item.soon && (
                  <span className="text-[9px] uppercase tracking-wider bg-brand-cyan/15 text-brand-cyan px-1.5 py-0.5 rounded-full">
                    Soon
                  </span>
                )}
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          {isEnabled("community") && (
            <Link to="/community" className="btn-primary px-5 py-2.5 text-sm" data-testid="nav-join-btn">
              Join The Project
            </Link>
          )}
        </div>

        <button
          className="xl:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden glass-nav border-t border-white/5 px-5 py-4" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center justify-between py-3 px-2 rounded-lg text-base font-medium ${
                    isActive ? "text-brand-cyan bg-white/5" : "text-brand-textSec"
                  }`
                }
              >
                {item.label}
                {item.soon && (
                  <span className="text-[9px] uppercase tracking-wider bg-brand-cyan/15 text-brand-cyan px-1.5 py-0.5 rounded-full">
                    Soon
                  </span>
                )}
              </NavLink>
            ))}
            {isEnabled("community") && (
              <Link to="/community" className="btn-primary text-center px-5 py-3 mt-3" data-testid="nav-mobile-join-btn">
                Join The Project
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
